import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Form, Table, Pagination } from "react-bootstrap";
import Select from "react-select";
import axios from "axios";
import { FiEye } from "react-icons/fi";
import { TranslatorContext } from "../../../context/Translator";
import PageLayout from "../../../layouts/PageLayout";
import { ButtonComponent } from "../../../components/elements";

const getTodayDate = () => new Date().toISOString().split("T")[0];

const debounce = (fn, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

export default function PurchaseIndex() {
  const { t } = useContext(TranslatorContext);
  const navigate = useNavigate();

  const [purchases, setPurchases] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: 10, total: 0 });

  const [filters, setFilters] = useState({
    vendor_id: "",
    purchase_number: "",
    status: "",
    start_date: getTodayDate(),
    end_date: getTodayDate(),
  });

  const permissions = useMemo(() => JSON.parse(localStorage.getItem("permissions")) || [], []);
  const isAdmin = useMemo(() => localStorage.getItem("role")?.toLowerCase() === "admin", []);

  const hasPermission = (perm) => permissions.includes(perm);

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/purchase-vendors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVendors(res.data.vendors || []);
    } catch (err) {
      console.error("Error fetching vendors:", err);
    }
  };

  const fetchPurchases = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const params = new URLSearchParams({
        page,
        per_page: pagination.per_page,
        ...(filters.vendor_id && { vendor_id: filters.vendor_id }),
        ...(filters.purchase_number && { purchase_number: filters.purchase_number }),
        ...(filters.status && { status: filters.status }),
        ...(filters.start_date && { start_date: filters.start_date }),
        ...(filters.end_date && { end_date: filters.end_date }),
      });

      const res = await axios.get(`${process.env.REACT_APP_API_URL}/purchases?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data.purchase;
      setPurchases(data?.data || []);
      setPagination({
        current_page: data.current_page,
        last_page: data.last_page,
        per_page: data.per_page,
        total: data.total,
      });
    } catch (err) {
      console.error("Error fetching purchases:", err);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.per_page]);

  const debouncedPurchaseSearch = useCallback(
    debounce((value) => setFilters((prev) => ({ ...prev, purchase_number: value })), 800),
    []
  );

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    fetchPurchases(1);
  }, [fetchPurchases]);

  const handlePurchaseNumberChange = (e) => {
    const { value } = e.target;
    setSearchInput(value);
    debouncedPurchaseSearch(value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleVendorChange = (selectedOption) => {
    setFilters((prev) => ({
      ...prev,
      vendor_id: selectedOption ? selectedOption.value : "",
      start_date: "",
      end_date: "",
    }));
  };

  const clearFilters = () => {
    setSearchInput("");
    setFilters({
      vendor_id: "",
      purchase_number: "",
      status: "",
      start_date: getTodayDate(),
      end_date: getTodayDate(),
    });
  };

  const vendorOptions = useMemo(
    () => [{ value: "", label: t("All Vendors") }, ...vendors.map((v) => ({ value: v.id, label: v.company_name }))],
    [vendors, t]
  );

  const selectedVendorOption = vendorOptions.find((opt) => opt.value === filters.vendor_id) || vendorOptions[0];

  const renderPaginationItems = () => {
    const items = [];
    const { current_page: current, last_page: last } = pagination;

    items.push(
      <Pagination.Prev key="prev" onClick={() => fetchPurchases(current - 1)} disabled={current === 1} />
    );

    let start = Math.max(1, current - 2);
    let end = Math.min(last, current + 2);

    if (start > 1) {
      items.push(<Pagination.Item key={1} onClick={() => fetchPurchases(1)}>1</Pagination.Item>);
      if (start > 2) items.push(<Pagination.Ellipsis key="el1" disabled />);
    }

    for (let page = start; page <= end; page++) {
      items.push(
        <Pagination.Item key={page} active={page === current} onClick={() => fetchPurchases(page)}>
          {page}
        </Pagination.Item>
      );
    }

    if (end < last) {
      if (end < last - 1) items.push(<Pagination.Ellipsis key="el2" disabled />);
      items.push(<Pagination.Item key={last} onClick={() => fetchPurchases(last)}>{last}</Pagination.Item>);
    }

    items.push(
      <Pagination.Next key="next" onClick={() => fetchPurchases(current + 1)} disabled={current === last} />
    );

    return items;
  };

  if (loading) return <PageLayout><p>{t("loading")}...</p></PageLayout>;

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">{t("purchases")}</h3>
              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">{t("home")}</Link>
                </li>
                <li className="mc-breadcrumb-item">{t("purchases")}</li>
              </ul>
            </div>
          </div>
        </Col>

        {isAdmin && (
          <Col xl={12}>
            <div className="mc-card p-3 mb-3">
              <Row className="align-items-end">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>{t("Purchase Number")}</Form.Label>
                    <Form.Control
                      type="text"
                      name="purchase_number"
                      value={searchInput}
                      onChange={handlePurchaseNumberChange}
                      placeholder={t("Search by purchase number...")}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>{t("Vendor")}</Form.Label>
                    <Select
                      name="vendor_id"
                      options={vendorOptions}
                      value={selectedVendorOption}
                      onChange={handleVendorChange}
                      placeholder={t("Select vendor...")}
                      isClearable
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>{t("Start Date")}</Form.Label>
                    <Form.Control
                      type="date"
                      name="start_date"
                      value={filters.start_date}
                      onChange={handleFilterChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>{t("End Date")}</Form.Label>
                    <Form.Control
                      type="date"
                      name="end_date"
                      value={filters.end_date}
                      onChange={handleFilterChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md={2} className="text-end">
                  <ButtonComponent type="button" className="btn btn-secondary" onClick={clearFilters}>
                    {t("Clear All Filters")}
                  </ButtonComponent>
                </Col>
              </Row>
            </div>
          </Col>
        )}

        <Col xl={12}>
          <div className="mc-card">
            <div className="table-responsive-wrapper">
              <Table responsive className="table mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t("purchase number")}</th>
                    <th>{t("vendor")}</th>
                    <th>{t("purchase date")}</th>
                    <th>{t("Products")}</th>
                    <th>{t("amount")}</th>
                    <th>{t("Action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4">{t("No purchases found")}</td>
                    </tr>
                  ) : (
                    purchases.map((purchase) => (
                      <tr key={purchase.id}>
                        <td>{purchase.id ?? "-"}</td>
                        <td><strong>{purchase.purchase_number}</strong></td>
                        <td>
                          <span
                            style={{ cursor: "pointer", color: "#0d6efd", fontWeight: "500" }}
                            onClick={() =>
                              setFilters((prev) => ({
                                ...prev,
                                vendor_id: purchase.vendor_id,
                                start_date: "",
                                end_date: "",
                              }))
                            }
                          >
                            {purchase.vendor_name || "-"}
                          </span>
                        </td>
                        <td>{purchase.purchase_date}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {purchase.purchase_summary?.slice(0, 3).map((item, i) => (
                              <span key={i} className="badge bg-light text-dark">
                                {item.name} × {item.qty}
                              </span>
                            ))}
                            {purchase.purchase_summary?.length > 5 && (
                              <span className="badge bg-secondary">
                                +{purchase.purchase_summary.length - 5} more
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="fw-bold">{purchase.total_amount?.toLocaleString()}</span>
                        </td>
                        <td>
                          <div className="mc-table-action">
                            {hasPermission("Purchases edit") && (
                              <ButtonComponent
                                type="button"
                                className="material-icons edit"
                                onClick={() => navigate(`/purchase-edit/${purchase.id}`)}
                                title="Edit"
                              >
                                edit
                              </ButtonComponent>
                            )}
                            <ButtonComponent
                              type="button"
                              className="material-icons view"
                              onClick={() => navigate(`/vendor-show/${purchase.vendor_id}`)}
                              title="View"
                            >
                              <FiEye />
                            </ButtonComponent>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            {pagination.last_page > 1 && (
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="text-muted small">
                  {t("Showing")} {(pagination.current_page - 1) * pagination.per_page + 1}
                  {t(" to ")} {Math.min(pagination.current_page * pagination.per_page, pagination.total)}
                  {t(" of ")} {pagination.total} {t("entries")}
                </div>
                <Pagination>{renderPaginationItems()}</Pagination>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </PageLayout>
  );
}